import { lazy, Suspense, Component, type ReactNode, type ErrorInfo, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate, useLocation } from "react-router-dom";
import { usePageTracking } from "@/hooks/usePageTracking";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { UserProvider } from "@/contexts/UserContext";
import { LangProvider } from "@/contexts/LangContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import { ThemeProvider } from "next-themes";
import ProtectedRoute from "@/components/ProtectedRoute";
import AuthRedirectHandler from "@/components/AuthRedirectHandler";
import HostnameRouter from "@/components/HostnameRouter";

import SidebarNav from "@/components/SidebarNav";
import BottomTabBar from "@/components/BottomTabBar";
import EmployerProtectedRoute from "@/components/employer/EmployerProtectedRoute";
import AdminProtectedRoute from "@/components/admin/AdminProtectedRoute";

import OfflineBanner from "@/components/OfflineBanner";
import TechvinexLoader from "@/components/TechvinexLoader";
import IdleTimeout from "@/components/security/IdleTimeout";
import SessionStatusIndicator from "@/components/SessionStatusIndicator";
import SessionExpiryBanner from "@/components/SessionExpiryBanner";
import CookieConsentBanner from "@/components/CookieConsentBanner";

// Error boundary to handle dynamic import failures (common in preview)
class LazyErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    if (error.message.includes('dynamically imported module')) {
      // Retry by reloading
      window.location.reload();
    }
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-[60vh] flex-col gap-4">
          <p className="text-muted-foreground text-sm">Loading failed. Retrying...</p>
          <button onClick={() => window.location.reload()} className="text-primary text-sm underline">
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Lazy loaded pages
const Index = lazy(() => import("./pages/Index"));
const Login = lazy(() => import("./pages/Login"));
const Explore = lazy(() => import("./pages/Explore"));
const RoleDetail = lazy(() => import("./pages/RoleDetail"));
const Referrals = lazy(() => import("./pages/Referrals"));
const Earnings = lazy(() => import("./pages/Earnings"));
const Profile = lazy(() => import("./pages/Profile"));
const EditProfile = lazy(() => import("./pages/EditProfile"));
const Settings = lazy(() => import("./pages/Settings"));
const ApplyRole = lazy(() => import("./pages/ApplyRole"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const Opportunities = lazy(() => import("./pages/Opportunities"));
const OpportunityDetail = lazy(() => import("./pages/OpportunityDetail"));
const About = lazy(() => import("./pages/About"));

const Notifications = lazy(() => import("./pages/Notifications"));
const CandidateDashboard = lazy(() => import("./pages/CandidateDashboard"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));

// AuthTest removed from production. Re-add behind import.meta.env.DEV if needed.
const EmployerLogin = lazy(() => import("./pages/employer/EmployerLogin"));
const EmployerDashboard = lazy(() => import("./pages/employer/EmployerDashboard"));
const EmployerRequirements = lazy(() => import("./pages/employer/EmployerRequirements"));
const EmployerCandidates = lazy(() => import("./pages/employer/EmployerCandidates"));
const EmployerInterviews = lazy(() => import("./pages/employer/EmployerInterviews"));
const EmployerAnalytics = lazy(() => import("./pages/employer/EmployerAnalytics"));
const EmployerBilling = lazy(() => import("./pages/employer/EmployerBilling"));
const EmployerPipeline = lazy(() => import("./pages/employer/EmployerPipeline"));
const EmployerTalentPool = lazy(() => import("./pages/employer/EmployerTalentPool"));
const EmployerSettings = lazy(() => import("./pages/employer/EmployerSettings"));
const EmployerMessages = lazy(() => import("./pages/employer/EmployerMessages"));
const EmployerNotifications = lazy(() => import("./pages/employer/EmployerNotifications"));
const EmployerLayout = lazy(() => import("./components/employer/EmployerLayout"));

// Admin pages
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const AdminOverview = lazy(() => import("./pages/admin/AdminOverview"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminRequirements = lazy(() => import("./pages/admin/AdminRequirements"));
const AdminCandidates = lazy(() => import("./pages/admin/AdminCandidates"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminAuditLogs = lazy(() => import("./pages/admin/AdminAuditLogs"));
const AdminNotifications = lazy(() => import("./pages/admin/AdminNotifications"));
const AdminPayments = lazy(() => import("./pages/admin/AdminPayments"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminAIMatching = lazy(() => import("./pages/admin/AdminAIMatching"));
const AdminExploreJobs = lazy(() => import("./pages/admin/AdminExploreJobs"));
const AdminBlogs = lazy(() => import("./pages/admin/AdminBlogs"));
const AdminSecrets = lazy(() => import("./pages/admin/AdminSecrets"));
const AdminWebhooks = lazy(() => import("./pages/admin/AdminWebhooks"));
const AdminAISecurity = lazy(() => import("./pages/admin/AdminAISecurity"));
const AdminMalwareScans = lazy(() => import("./pages/admin/AdminMalwareScans"));
const ExploreJobDetail = lazy(() => import("./pages/ExploreJobDetail"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogDetail = lazy(() => import("./pages/BlogDetail"));
const BlogDashboardPage = lazy(() => import("./pages/blog-dashboard/BlogDashboard"));

// Benchmark pages
const BenchmarkLayout = lazy(() => import("./components/benchmark/BenchmarkLayout"));
const BenchmarkProtectedRoute = lazy(() => import("./components/benchmark/BenchmarkProtectedRoute"));
const BenchmarkDashboard = lazy(() => import("./pages/benchmark/BenchmarkDashboard"));
const BenchmarkTasks = lazy(() => import("./pages/benchmark/BenchmarkTasks"));
const BenchmarkRunTest = lazy(() => import("./pages/benchmark/BenchmarkRunTest"));
const BenchmarkResults = lazy(() => import("./pages/benchmark/BenchmarkResults"));
const BenchmarkLeaderboard = lazy(() => import("./pages/benchmark/BenchmarkLeaderboard"));
const BenchmarkReviews = lazy(() => import("./pages/benchmark/BenchmarkReviews"));
const BenchmarkSettings = lazy(() => import("./pages/benchmark/BenchmarkSettings"));
const BenchmarkLogin = lazy(() => import("./pages/benchmark/BenchmarkLogin"));
const BlogDashboardProtectedRoute = lazy(() => import("./components/blog-dashboard/BlogDashboardProtectedRoute"));

const queryClient = new QueryClient();

const TABS_VISIBLE_ROUTES = ["/", "/explore", "/dashboard", "/referrals", "/earnings", "/profile"];

const hideSplashScreen = () => {
  const splash = document.getElementById("splash-screen");
  if (!splash) return;

  splash.style.opacity = "0";
  splash.style.pointerEvents = "none";
  window.setTimeout(() => splash.remove(), 220);
};

const PageLoader = () => <TechvinexLoader />;

const AppLayout = () => {
  const location = useLocation();
  const showTabs = TABS_VISIBLE_ROUTES.includes(location.pathname);

  usePageTracking();

  useEffect(() => {
    // Hide splash screen after a short delay to allow React to render
    const timer = window.setTimeout(() => {
      hideSplashScreen();
    }, 300);

    return () => window.clearTimeout(timer);
  }, []);

  return (
    <>
      <HostnameRouter />
      <AuthRedirectHandler />
      <SessionExpiryBanner />
      {showTabs && <SidebarNav />}
      {showTabs && <BottomTabBar />}
      <LazyErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <main id="main-content">
        <Routes>
          <Route path="/" element={<Explore />} />
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
          {/* Alias for the OAuth authorization path configured on the Supabase auth server */}
          <Route path="/oauth/consent" element={<OAuthConsent />} />

          {import.meta.env.DEV && <Route path="/auth-test" element={<div>AuthTest removed from production build.</div>} />}
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/home" element={<ProtectedRoute><Index /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><CandidateDashboard /></ProtectedRoute>} />
          <Route path="/explore" element={<Explore />} />
          <Route path="/role/:id" element={<ProtectedRoute><RoleDetail /></ProtectedRoute>} />
          <Route path="/referrals" element={<ProtectedRoute><Referrals /></ProtectedRoute>} />
          <Route path="/earnings" element={<ProtectedRoute><Earnings /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/edit-profile" element={<ProtectedRoute><EditProfile /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="/apply/:id" element={<ProtectedRoute><ApplyRole /></ProtectedRoute>} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/about" element={<About />} />
          <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
          <Route path="/jobs/:slug" element={<OpportunityDetail />} />
          <Route path="/opportunities" element={<Opportunities />} />
          <Route path="/opportunities/:slug" element={<OpportunityDetail />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogDetail />} />
          <Route path="/blog-dashboard/login" element={<Navigate to="/admin/login" replace />} />
          <Route path="/blog-dashboard" element={
            <Suspense fallback={<PageLoader />}>
              <BlogDashboardProtectedRoute><BlogDashboardPage /></BlogDashboardProtectedRoute>
            </Suspense>
          } />

          {/* Employer Portal */}
          <Route path="/employer/login" element={<EmployerLogin />} />
          <Route path="/employer" element={<EmployerProtectedRoute><EmployerLayout /></EmployerProtectedRoute>}>
            <Route path="dashboard" element={<EmployerDashboard />} />
            <Route path="requirements" element={<EmployerRequirements />} />
            <Route path="candidates" element={<EmployerCandidates />} />
            <Route path="interviews" element={<EmployerInterviews />} />
            <Route path="analytics" element={<EmployerAnalytics />} />
            <Route path="billing" element={<EmployerBilling />} />
            <Route path="pipeline" element={<EmployerPipeline />} />
            <Route path="talent-pool" element={<EmployerTalentPool />} />
            <Route path="settings" element={<EmployerSettings />} />
            <Route path="messages" element={<EmployerMessages />} />
            <Route path="notifications" element={<EmployerNotifications />} />
          </Route>

          {/* Benchmark Platform */}
          <Route path="/benchmark/login" element={<Suspense fallback={<PageLoader />}><BenchmarkLogin /></Suspense>} />
          <Route path="/benchmark" element={
            <Suspense fallback={<PageLoader />}>
              <BenchmarkProtectedRoute><BenchmarkLayout /></BenchmarkProtectedRoute>
            </Suspense>
          }>
            <Route path="dashboard" element={<BenchmarkDashboard />} />
            <Route path="results" element={<BenchmarkResults />} />
            <Route path="leaderboard" element={<BenchmarkLeaderboard />} />
            {/* Admin/reviewer only surfaces */}
            <Route path="tasks" element={
              <BenchmarkProtectedRoute requiredRoles={["admin", "reviewer"]}><BenchmarkTasks /></BenchmarkProtectedRoute>
            } />
            <Route path="run" element={
              <BenchmarkProtectedRoute requiredRoles={["admin", "reviewer"]}><BenchmarkRunTest /></BenchmarkProtectedRoute>
            } />
            <Route path="reviews" element={
              <BenchmarkProtectedRoute requiredRoles={["admin", "reviewer"]}><BenchmarkReviews /></BenchmarkProtectedRoute>
            } />
            <Route path="settings" element={
              <BenchmarkProtectedRoute requiredRoles={["admin"]}><BenchmarkSettings /></BenchmarkProtectedRoute>
            } />
          </Route>


          {/* Admin Panel */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminProtectedRoute><AdminLayout /></AdminProtectedRoute>}>
            <Route path="overview" element={<AdminOverview />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="requirements" element={<AdminRequirements />} />
            <Route path="candidates" element={<AdminCandidates />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="audit-logs" element={<AdminAuditLogs />} />
            <Route path="notifications" element={<AdminNotifications />} />
            <Route path="payments" element={<AdminPayments />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="ai-matching" element={<AdminAIMatching />} />
            <Route path="explore-jobs" element={<AdminExploreJobs />} />
            <Route path="blogs" element={<AdminBlogs />} />
            <Route path="secrets" element={<AdminSecrets />} />
            <Route path="webhooks" element={<AdminWebhooks />} />
            <Route path="ai-security" element={<AdminAISecurity />} />
            <Route path="malware-scans" element={<AdminMalwareScans />} />
          </Route>


          <Route path="*" element={<NotFound />} />
        </Routes>
        </main>
      </Suspense>
      </LazyErrorBoundary>

      <SessionStatusIndicator />
      <CookieConsentBanner />
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} forcedTheme="light">
    <AppErrorBoundary>
    <AuthProvider>
    <UserProvider>
    <LangProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <OfflineBanner />
        <BrowserRouter>
          {/* IdleTimeout consumes useAuth() and must live inside AuthProvider.
              It also needs to be inside BrowserRouter so that any future
              navigation it triggers resolves via the router. */}
          <IdleTimeout />
          <AppLayout />
        </BrowserRouter>
      </TooltipProvider>
    </LangProvider>
    </UserProvider>
    </AuthProvider>
    </AppErrorBoundary>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
